import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { NgcContentOptions, NgcCookieConsentService } from 'ngx-cookieconsent';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { appCommonConstants } from './app.constants';
import { IAppAuthDataType } from './interfaces/auth.interfaces';
import { appRsMapConstants } from './modules/rs-map/constants/rs-map.constants';
import { appRsMapOptions } from './modules/rs-map/services/rs-map-utils';
import { animationEnabled, getGuid } from './services/apphelper';
import { AppAuthService } from './services/auth.service';
import { AppAuthMsalService } from './services/authMsalService';
import { AppDialogService } from './services/dialog.service';
import { AppEventService } from './services/event.service';
import { AppLoggerService } from './services/logger.service';
import { AppVersionCheckService } from './services/versionCheck.service';

@Component({
  selector: 'body',
  template: `<router-outlet></router-outlet>`,
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnDestroy, OnInit {
  constructor(
    private activatedRoute: ActivatedRoute,
    private authMsalService: AppAuthMsalService,
    private authService: AppAuthService,
    private cookieConsent: NgcCookieConsentService, // Inject the NgcCookieContentService in your main component (i.e. AppComponent) to show the cookie consent popup after the component is loaded. You don't need to explicitly call its init() method (done automatically when the service's constructor gets called upon instantiation By Angular).
    private dialogService: AppDialogService,
    private eventService: AppEventService,
    private logger: AppLoggerService,
    private router: Router,
    private titleService: Title,
    @Inject(TranslateService) private translate: TranslateService,
    private versionCheckService: AppVersionCheckService) {
    /** Impostazione variabili globali (guid della finestra) */
    (window as any)[appCommonConstants.global.guid] = this.windowGuid;
  }

  private languageSelectionSubscription: Subscription = new Subscription();
  private mediaQueryList?: MediaQueryList;
  private windowGuid: string = getGuid();

  ngOnDestroy() {
    this.mediaQueryList?.removeEventListener('change', this.mediaQueryCallback);
  }

  ngOnInit() {
    this.registerEvents();
    this.cookieConsentInit();
    this.authMsalService.init();  // Inizializzazione login Azure AD B2C

    // Crea il MediaQueryList
    this.mediaQueryList = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mediaQueryList.addEventListener('change', this.mediaQueryCallback);
    setTimeout(() => {
      if (!animationEnabled()) {
        this.logger.warning({ message: this.translate.instant('MESSAGE.Animazioni ridotte o disabilitate dalle impostazioni di sistema') });
      }

      /**
       * Inclusione delle api google
       * Ref: https://developers.google.com/maps/documentation/javascript/examples/streetview-simple
       */
      const script = document.createElement("script");
      script.setAttribute("async", "");
      script.setAttribute("src", `${appRsMapConstants.googleApiUrl}${appRsMapOptions().googleApiKey}&callback=googleMapInit`);
      document.body.appendChild(script);
    });

    /** Impostazione variabili globali con le informazioni base dell'applicazione */
    (window as any)[appCommonConstants.global.appInfo] = {
      environment: environment,
      ngVersion: document.querySelector('body')?.getAttribute("ng-version") ?? '', // versione angular in uso
      version: environment.appSettings.appVersion
    };

    // Abilitazione version checker
    if (environment.versionCheck) {
      this.versionCheckService.init("version.json");
    }

    // Gestione degli eventi di routing
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(e => {
        // Rimuove l'eventuale overlay aggiunto da AppDialogService.windowOpen()
        let newdiv: HTMLElement | null = document.getElementById(appCommonConstants.dialog.overlayId);
        if (newdiv) {
          newdiv.remove();
        }
        document.body.classList.remove('overflow-hidden');
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          let child = this.activatedRoute.firstChild;
          while (child) {
            if (child.firstChild) {
              child = child.firstChild;
            } else if (child.snapshot.data && child.snapshot.data['title']) {
              return child.snapshot.data['title'];
            } else {
              return null;
            }
          }
          return null;
        }))
      .subscribe((data: any) => {
        let setTitle = (title: string, data: string): void => {
          if (data != null) {
            title = `${title} - ${this.translate.instant(data)}`;
          }

          this.titleService.setTitle(title);
        }

        let title: string = environment.appSettings.appTitle != null ? environment.appSettings.appTitle : '';
        if (this.languageSelectionSubscription) {
          this.languageSelectionSubscription.unsubscribe();
        }
        this.languageSelectionSubscription = this.eventService.on(appCommonConstants.events.languageSelection).subscribe(val => {
          setTitle(title, data);
        });

        setTitle(title, data);
        window.scrollTo(0, 0);
      });
  }

  /**
   * Inizializzazione componente per Cookie Law, incluso l'impostazione delle etichette
   * Ref: https://github.com/tinesoft/ngx-cookieconsent#i18n-support
   */
  private cookieConsentInit(): void {
    let cookieUrl = (_.find(environment.policy, x => x.isCookie) ?? {}).url;
    // Nel caso non fosse impostato alcun url per la cookie policy, rimuove il banner di visualizzazione ed esce
    if (!cookieUrl) {
      if (this.cookieConsent['popupInstance']) {
        if (this.cookieConsent.hasConsented()) {
          this.cookieConsent.destroy();
        }
      }
      return;
    }

    // Impostazione etichette CookieLaw
    this.translate.onLangChange.subscribe((evt) => {
      let config = this.cookieConsent.getConfig();
      config.content = config.content ?? new NgcContentOptions();

      for (let label in config.content) {
        config.content[label] = this.translate.instant(`COOKIECONSENT.${label.toUpperCase()}`);
      }
      config.content["href"] = cookieUrl;

      this.cookieConsent.destroy(); // remove previous cookie bar (with default messages)
      this.cookieConsent.init(config); // update config with translated messages
    });
  }

  /**
   * Callback per il MediaQueryList, per gestire le animazioni in base alle impostazioni di sistema
   * @param e Evento MediaQueryListEvent
   */
  private mediaQueryCallback = (e: MediaQueryListEvent): void => {
    if (!animationEnabled()) {
      this.logger.warning({ message: this.translate.instant('MESSAGE.Animazioni ridotte o disabilitate dalle impostazioni di sistema') });
    } else {
      this.logger.info({ message: this.translate.instant('MESSAGE.Animazioni abilitate normalmente') });
    }
  }

  private registerEvents(): void {
    // Evento di login
    this.eventService.onChannelMessage(appCommonConstants.events.loggedin, 'perrequest', (x: { authData: IAppAuthDataType; sender: string }) => {
      if (x.sender === this.windowGuid) {
        return;
      }

      let doIt = (): void => {
        location.reload();
      };

      // Visualizza dialog informativa, per reload pagina o relaoad manuale
      let dialog = this.dialogService.dialogOpen({
        actions: [
          {
            text: this.translate.instant('WORD.RICARICA_PAGINA'),
            themeColor: "primary"
          }],
        content: `<div class="p-2">${this.translate.instant("MESSAGE.LOGIN_OTHER_PAGE", { username: x.authData.userName })}</div>`, // "È stato effettuato il login per l'utente <b>{{userName}}</b> su un'altra pagina o scheda del browser<br />Ricaricare la pagina per ottenere i dati aggiornati."
        cssClassList: ['dialog-info'],
        isTitleHtml: true,
        maxWidth: window.innerWidth > 1024 ? 550 : 400,
        minHeight: 300,
        minWidth: 450,
        title: `<span><i class="fas fa-sign-in-alt"></i>&nbsp;${this.translate.instant('WORD.RICARICA_PAGINA')}</span>`
      });

      dialog.result.subscribe((result: any) => {
        doIt();
      });
    });

    // Evento di logout
    this.eventService.onChannelMessage(appCommonConstants.events.logoff, 'perrequest', (x: { sender: string }) => {
      if (x.sender === this.windowGuid) {
        return;
      }

      this.logger.info({ message: this.translate.instant("MESSAGE.LOGOUT.REDIRECT"), title: this.translate.instant("WORD.LOGOUT") });
      setTimeout(() => this.authService.gotoLogin());
    });
  }
}
